import { mapApi } from '../../requests/api';

// The parcel boundaries drawn over the map.
//
// Real polygons, not image tiles, because the design needs each parcel to
// answer to the pointer on its own: hover it, click it, tint it by whether
// iCLIMB already holds it, grey it out when it is dropped from a selection.
//
// Below PARCEL_MIN_ZOOM a viewport holds more parcels than a polygon layer can
// carry, so nothing is drawn and the view says so.

export const PARCEL_MIN_ZOOM = 16;

// More than this in view and the layer would be drawing an arbitrary subset,
// which is worse than drawing nothing.
const PAGE_SIZE = 2000;

// How many polygons to keep alive across pans, viewport included.
//
// Panning used to throw away every polygon and build the next viewport's from
// scratch — two thousand `new google.maps.Polygon` calls with four listeners
// each, for a set that mostly overlapped what was already on screen. Parcels
// are now kept and reused, so a pan only builds what has actually come into
// view and panning back is instant. The cap is what stops that from growing
// without bound on a long session; three viewports' worth is roughly 12 MB of
// overlay and covers panning back and forth over the same few blocks.
const MAX_POLYGONS = PAGE_SIZE * 3;

// How many fetched viewports to remember, so panning back over ground already
// covered costs nothing. Kept below the polygon cap deliberately: a region is
// only worth skipping while its parcels are still drawn, and evict() throws the
// list away whenever it actually removes any.
const MAX_REGIONS = 3;

// How much beyond the viewport to fetch, as a share of its size. Panning a
// short distance then stays inside what is already loaded.
const MARGIN = 0.2;

// Idle fires on every zoom step and every pan. Waiting for the map to settle
// turns a pinch-zoom across four levels into one request instead of four.
const SETTLE_MS = 450;

export default class ParcelLayer {
    constructor(map, { onStatus, onHover, onSelect, onQuickAdd, includeResidential }) {
        this.map = map;
        this.onStatus = onStatus;
        this.onHover = onHover;
        this.onSelect = onSelect;
        this.onQuickAdd = onQuickAdd;

        this.includeResidential = Boolean(includeResidential);
        this.enabled = true;
        this.interactive = true;
        this.outlines = [];
        this.byId = new Map();
        this.polygons = new Map();
        // Boundary rings and their box, per parcel, for point lookups.
        this.shapes = new Map();
        this.visibleIds = new Set();
        this.loadedBounds = null;
        this.loadedRegions = [];
        this.loadedZoom = null;
        this.requestId = 0;
        this.inFlight = false;
        this.settleTimer = null;

        this.listener = map.addListener('idle', () => this.scheduleRefresh());
    }

    destroy() {
        if (this.listener) this.listener.remove();
        clearTimeout(this.settleTimer);
        this.clear();
    }

    setEnabled(enabled) {
        this.enabled = enabled;

        if (!enabled) {
            clearTimeout(this.settleTimer);
            this.abandon();
            this.report('off', 0);
            return;
        }

        this.scheduleRefresh();
    }

    /**
     * Show or hide the land use groups the map leaves out by default.
     *
     * Drops what is loaded first. refresh() short-circuits when the viewport is
     * already covered by the last fetch, so without abandon() the toggle would
     * do nothing at all until the user panned.
     */
    setIncludeResidential(includeResidential) {
        if (this.includeResidential === Boolean(includeResidential)) return;

        this.includeResidential = Boolean(includeResidential);
        this.abandon();
        this.scheduleRefresh();
    }

    /**
     * Hold off until the map has stopped moving.
     *
     * Zooming steps through levels and each one settles, so without this a
     * user zooming from 19 to 16 fires four viewport requests and pays for
     * three of them twice over.
     */
    scheduleRefresh() {
        clearTimeout(this.settleTimer);

        if (!this.enabled) return;

        const zoom = this.map.getZoom();

        // Leaving the polygon range is immediate: it only clears what is drawn,
        // costs no request, and waiting would leave stale outlines on screen.
        if (zoom < PARCEL_MIN_ZOOM) {
            this.abandon();
            this.report('zoom', 0);
            return;
        }

        this.settleTimer = setTimeout(() => this.refresh(), SETTLE_MS);
    }

    /** Drawing a shape must not be intercepted by the parcel underneath it. */
    setInteractive(interactive) {
        this.interactive = interactive;
        this.polygons.forEach((polygon) => polygon.setOptions({ clickable: interactive }));
    }

    /** The outline record for one parcel, or undefined. */
    get(parcelId) {
        return this.byId.get(parcelId);
    }

    getOutlines() {
        return this.outlines;
    }

    /**
     * The drawn parcels these points stand inside: the answer parcelAt gives
     * for a property's own coordinate, read off the outlines already here.
     *
     * @param {Array<{lat: number, lng: number}>} points
     * @returns {Set<string>} parcel ids
     */
    parcelsUnder(points) {
        const found = new Set();
        const shapes = this.outlines.map((outline) => this.shapeOf(outline)).filter(Boolean);

        if (shapes.length === 0) return found;

        const extent = shapes.reduce((box, shape) => ({
            south: Math.min(box.south, shape.south),
            north: Math.max(box.north, shape.north),
            west: Math.min(box.west, shape.west),
            east: Math.max(box.east, shape.east),
        }));

        points.forEach((point) => {
            if (!boxContains(extent, point)) return;

            const shape = shapes.find((candidate) => boxContains(candidate, point) && ringsContain(candidate.paths, point));

            if (shape) found.add(shape.parcelId);
        });

        return found;
    }

    shapeOf(outline) {
        if (!this.shapes.has(outline.parcel_id)) {
            const paths = wktToPaths(outline.boundary_wkt);
            const points = [].concat(...paths);

            this.shapes.set(outline.parcel_id, points.length === 0 ? null : {
                parcelId: outline.parcel_id,
                paths,
                south: Math.min(...points.map((point) => point.lat)),
                north: Math.max(...points.map((point) => point.lat)),
                west: Math.min(...points.map((point) => point.lng)),
                east: Math.max(...points.map((point) => point.lng)),
            });
        }

        return this.shapes.get(outline.parcel_id);
    }

    /**
     * Re-colour without re-fetching. The container owns what a parcel looks
     * like — it is the only thing that knows what is hovered, selected, or
     * dropped from a shape — so it hands the style down.
     */
    restyle(styleFor) {
        this.styleFor = styleFor;
        this.polygons.forEach((polygon, parcelId) => polygon.setOptions(styleFor(parcelId)));
    }

    /**
     * Re-colour named parcels only.
     *
     * Moving the pointer between two parcels changes the look of exactly those
     * two, and restyling the whole layer for it meant several thousand
     * setOptions calls per mouse-over — enough to make hovering feel heavy on a
     * dense block.
     */
    restyleOnly(parcelIds) {
        if (!this.styleFor) return;

        parcelIds.forEach((parcelId) => {
            const polygon = this.polygons.get(parcelId);

            if (polygon) polygon.setOptions(this.styleFor(parcelId));
        });
    }

    refresh() {
        if (!this.enabled) return;

        const zoom = this.map.getZoom();
        const bounds = this.map.getBounds();

        if (!bounds) return;

        if (zoom < PARCEL_MIN_ZOOM) {
            this.abandon();
            this.report('zoom', 0);
            return;
        }

        // Covered by ground already fetched — nothing to ask for. Zoom is
        // deliberately not part of this: a parcel boundary is the same shape at
        // every zoom, so zooming in inside a loaded area used to pay for a
        // metered request that returned the parcels already on screen. And it
        // is every remembered region rather than only the last, because panning
        // east and back west is the commonest thing a user does here.
        if (this.isCovered(bounds)) {
            this.report('ready', this.countWithin(bounds));
            return;
        }

        // One metered request at a time. Whatever the map is showing when this
        // one lands is what gets asked for next, so nothing is queued up.
        if (this.inFlight) {
            this.refreshWhenFree = true;
            return;
        }

        this.load(this.padded(bounds), zoom);
    }

    load(bounds, zoom) {
        const requestId = ++this.requestId;

        this.inFlight = true;
        this.report('loading', this.outlines.length);

        mapApi
            .parcelOutlines(this.boundsToWkt(bounds), PAGE_SIZE, this.includeResidential)
            .then((response) => {
                if (requestId !== this.requestId) return;

                const total = (response.meta && response.meta.total) || response.data.length;

                // Drawing an arbitrary first two thousand of a much larger set
                // would misrepresent the area, so draw none and say why.
                if (total > PAGE_SIZE) {
                    this.clear();
                    this.loadedBounds = null;
                    this.report('dense', total);
                    return;
                }

                this.loadedBounds = bounds;
                this.loadedZoom = zoom;
                this.remember(bounds);
                this.merge(response.data);
                // The count is what is in view, not what the layer is holding:
                // parcels kept from an earlier pan are off screen.
                this.report('ready', response.data.length);
            })
            .catch((error) => {
                if (requestId !== this.requestId) return;

                // The view shows a short line; the upstream detail is only
                // useful to whoever is debugging, so it goes here.
                console.warn('Parcel outlines failed:', error.message);
                this.report('error', 0, error.message);
            })
            .then(() => {
                this.inFlight = false;

                if (this.refreshWhenFree) {
                    this.refreshWhenFree = false;
                    this.refresh();
                }
            });
    }

    /**
     * Fold a viewport's parcels into what is already drawn.
     *
     * A parcel already on the map is left exactly as it is — the geometry does
     * not change, so rebuilding it would only cost a polygon and drop the
     * pointer state along with it. Its outline record is refreshed though,
     * because whether iCLIMB holds the parcel can have changed since the pan
     * that first drew it.
     */
    merge(outlines) {
        this.visibleIds = new Set();

        outlines.forEach((outline) => {
            this.visibleIds.add(outline.parcel_id);

            const known = this.polygons.get(outline.parcel_id);

            if (known) {
                this.byId.set(outline.parcel_id, outline);
                if (this.styleFor) known.setOptions(this.styleFor(outline.parcel_id));
                return;
            }

            const paths = wktToPaths(outline.boundary_wkt);

            if (paths.length === 0) return;

            this.byId.set(outline.parcel_id, outline);

            const polygon = new google.maps.Polygon({
                map: this.map,
                paths,
                clickable: this.interactive,
                zIndex: 1,
                ...(this.styleFor ? this.styleFor(outline.parcel_id) : {}),
            });

            polygon.addListener('mouseover', () => this.onHover(outline.parcel_id));
            polygon.addListener('mouseout', () => this.onHover(null));
            polygon.addListener('click', (event) => this.onSelect(event.latLng));
            polygon.addListener('dblclick', (event) => this.onQuickAdd(event.latLng));

            this.polygons.set(outline.parcel_id, polygon);
        });

        this.evict();
        this.outlines = Array.from(this.byId.values());
    }

    /**
     * Drop the least recently drawn parcels once past the cap.
     *
     * Map preserves insertion order, so the front of it is the oldest pan.
     * Anything in the current viewport is skipped however old it is — evicting
     * what the user is looking at would undo the point of keeping any of it.
     */
    evict() {
        if (this.polygons.size <= MAX_POLYGONS) return;

        let over = this.polygons.size - MAX_POLYGONS;

        for (const parcelId of Array.from(this.polygons.keys())) {
            if (over <= 0) break;
            if (this.visibleIds.has(parcelId)) continue;

            this.polygons.get(parcelId).setMap(null);
            this.polygons.delete(parcelId);
            this.byId.delete(parcelId);
            this.shapes.delete(parcelId);
            over--;
        }

        // Anything evicted makes the remembered regions a lie: their bounds
        // would still say "covered" over ground whose parcels have just been
        // dropped, and the layer would skip the fetch and draw nothing there.
        this.loadedRegions = this.loadedBounds ? [this.loadedBounds] : [];
    }

    /** Note a viewport as fetched, keeping only the most recent few. */
    remember(bounds) {
        this.loadedRegions = this.loadedRegions
            .filter((region) => !boundsEqual(region, bounds))
            .concat([bounds])
            .slice(-MAX_REGIONS);
    }

    isCovered(bounds) {
        return this.loadedRegions.some((region) =>
            region.contains(bounds.getNorthEast()) && region.contains(bounds.getSouthWest()));
    }

    /**
     * How many of the parcels held sit in the given bounds.
     *
     * Needed because the layer now keeps parcels from earlier pans: the status
     * bar says "in view", and the number it is given has to mean that rather
     * than however many the layer happens to be holding. Counted off the
     * representative point of each boundary, which is enough for a count.
     */
    countWithin(bounds) {
        let count = 0;

        this.byId.forEach((outline) => {
            const point = firstPoint(outline.boundary_wkt);

            if (point && bounds.contains(new google.maps.LatLng(point.lat, point.lng))) count++;
        });

        return count;
    }

    clear() {
        this.polygons.forEach((polygon) => polygon.setMap(null));
        this.polygons.clear();
        this.byId.clear();
        this.shapes.clear();
        this.outlines = [];
        this.visibleIds = new Set();
        // Must go with the polygons. A remembered region whose parcels have
        // been dropped answers "covered" and the layer then skips the fetch and
        // draws nothing — which is what toggling Houses does to the whole layer.
        this.loadedRegions = [];
    }

    /**
     * Drop what is drawn and disown any request still in the air.
     *
     * Without the id bump, zooming out while a fetch is running lets that
     * fetch land afterwards and report on a viewport that is no longer shown.
     */
    abandon() {
        this.requestId++;
        this.clear();
        this.loadedBounds = null;
    }

    report(status, count, message) {
        this.onStatus({ status, count, message });
    }

    padded(bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const latPad = (ne.lat() - sw.lat()) * MARGIN;
        const lngPad = (ne.lng() - sw.lng()) * MARGIN;

        return new google.maps.LatLngBounds(
            { lat: sw.lat() - latPad, lng: sw.lng() - lngPad },
            { lat: ne.lat() + latPad, lng: ne.lng() + lngPad }
        );
    }

    boundsToWkt(bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const corners = [
            [sw.lng(), sw.lat()],
            [ne.lng(), sw.lat()],
            [ne.lng(), ne.lat()],
            [sw.lng(), ne.lat()],
            [sw.lng(), sw.lat()],
        ];

        return 'POLYGON((' + corners.map(([lng, lat]) => lng + ' ' + lat).join(', ') + '))';
    }
}

/** Same ground, to the metre. Google's LatLngBounds has no equality test. */
function boundsEqual(a, b) {
    return a.getNorthEast().equals(b.getNorthEast()) && a.getSouthWest().equals(b.getSouthWest());
}

function boxContains(box, point) {
    return point.lat >= box.south && point.lat <= box.north && point.lng >= box.west && point.lng <= box.east;
}

/**
 * Even-odd over every ring at once, so a hole in a lot reads as outside it
 * and each piece of a split lot as inside.
 */
function ringsContain(paths, point) {
    let inside = false;

    paths.forEach((ring) => {
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const a = ring[i];
            const b = ring[j];

            if ((a.lat > point.lat) !== (b.lat > point.lat)
                && point.lng < ((b.lng - a.lng) * (point.lat - a.lat)) / (b.lat - a.lat) + a.lng) {
                inside = !inside;
            }
        }
    });

    return inside;
}

/** The first vertex of a boundary — enough to place a parcel for counting. */
function firstPoint(wkt) {
    const pair = (wkt || '').match(/(-?\d+\.\d+)\s+(-?\d+\.\d+)/);

    return pair ? { lng: Number(pair[1]), lat: Number(pair[2]) } : null;
}

/**
 * WKT to Google paths.
 *
 * Only the shapes a parcel boundary is ever returned as: POLYGON, and
 * MULTIPOLYGON for the split lots that come back as more than one ring.
 */
export function wktToPaths(wkt) {
    if (!wkt) return [];

    const rings = wkt.match(/\(([-\d.,\s]+)\)/g);

    if (!rings) return [];

    return rings
        .map((ring) =>
            ring
                .replace(/[()]/g, '')
                .split(',')
                .map((pair) => {
                    const [lng, lat] = pair.trim().split(/\s+/).map(Number);
                    return { lat, lng };
                })
                .filter((point) => !isNaN(point.lat) && !isNaN(point.lng))
        )
        .filter((path) => path.length >= 3);
}
